<template>
  <b-modal v-model="isActive" has-modal-card trap-focus :can-cancel="['escape', 'outside']">
    <div class="modal-card" style="width: 1200px; max-width: 98%;">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ $t('warehouse_material_merge_duplicates') }}</p>
        <button type="button" class="delete" @click="close" />
      </header>
      <section class="modal-card-body">
        <b-loading :active="isLoading" :is-full-page="false" />

        <p class="mb-4">{{ $t('warehouse_material_merge_duplicates_intro') }}</p>

        <template v-if="!isLoading && groups.length === 0">
          <div class="has-text-centered has-text-grey my-6">
            {{ $t('warehouse_material_merge_no_duplicates') }}
          </div>
        </template>

        <!-- Un bloque por grupo de duplicados. Layout con utilidades Tailwind;
             la tabla reutiliza .epc-data-table (global). -->
        <div
          v-for="(group, gi) in groups"
          :key="`group-${gi}-${group.normalized_name}`"
          class="mb-5 p-3 bg-gray-100 border border-gray-300 rounded"
        >
          <div class="flex items-center gap-2 mb-2 text-blue-900 font-semibold">
            <b-icon pack="fas" icon="layer-group" size="is-small" />
            <span>{{ group.display_name }}</span>
            <b-tag rounded>{{ group.materials.length }}</b-tag>
          </div>

          <div class="epc-data-table">
            <table class="table is-fullwidth">
              <thead>
                <tr>
                  <th>{{ $t('warehouse_material_merge_use_as_canonical') }}</th>
                  <th>{{ $t('unique_material_id') }}</th>
                  <th>{{ $t('description') }}</th>
                  <th>{{ $t('category') }}</th>
                  <th>{{ $t('order_number') }}</th>
                  <th class="has-text-right">{{ $t('warehouse_inventory_lines') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in group.materials" :key="`m-${m.id}`">
                  <td class="has-text-centered">
                    <b-radio
                      v-model="selectedCanonical[group.normalized_name]"
                      :native-value="m.id"
                    />
                  </td>
                  <td>{{ m.code }}</td>
                  <td>{{ m.description }}</td>
                  <td>{{ m.category || '-' }}</td>
                  <td>{{ m.order_number || '-' }}</td>
                  <td class="has-text-right">{{ m.inventory_lines_count }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="flex justify-end mt-2">
            <v-button
              :disabled="!selectedCanonical[group.normalized_name] || isMerging"
              :loading="mergingGroup === group.normalized_name"
              color="green"
              icon="object-group"
              icon-pack="fas"
              @click="mergeGroup(group)"
            >
              {{ $t('warehouse_material_merge_group') }}
            </v-button>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <v-button @click="close" color="dark">{{ $t('close') }}</v-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'

export default {
  name: 'warehouse-merge-duplicates-modal',
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    isActive: false,
    isLoading: false,
    isMerging: false,
    mergingGroup: null,
    groups: [],
    // Mapa normalized_name → id del material elegido como canónico.
    selectedCanonical: {},
  }),
  methods: {
    async open() {
      this.isActive = true
      await this.loadDuplicates()
    },
    close() {
      this.isActive = false
    },
    async loadDuplicates() {
      if (!this.projectId) return
      this.isLoading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/warehouse/materials/duplicates`,
        )
        const groups = (data && Array.isArray(data.groups)) ? data.groups : []
        this.groups = groups
        // Preseleccionamos como canónico el material con más líneas de inventario.
        const next = {}
        for (const g of groups) {
          if (g && Array.isArray(g.materials) && g.materials.length > 0) {
            next[g.normalized_name] = g.materials[0].id
          }
        }
        this.selectedCanonical = next
      } catch (e) {
        this.groups = []
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoading = false
      }
    },
    async mergeGroup(group) {
      if (!this.projectId || !group) return
      const canonicalId = this.selectedCanonical[group.normalized_name]
      if (!canonicalId) return

      const secondaryIds = (group.materials || [])
        .map((m) => Number(m.id))
        .filter((id) => Number.isFinite(id) && id !== Number(canonicalId))

      if (secondaryIds.length === 0) return

      const confirmed = await new Promise((resolve) => {
        this.$buefy.dialog.confirm({
          title: this.$t('warehouse_material_merge_duplicates'),
          message: this.$t('warehouse_material_merge_confirm', {
            count: secondaryIds.length,
            name: group.display_name,
          }),
          cancelText: this.$t('cancel'),
          confirmText: this.$t('warehouse_material_merge_confirm_action'),
          type: 'is-warning',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
      if (!confirmed) return

      this.isMerging = true
      this.mergingGroup = group.normalized_name
      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/warehouse/materials/merge`,
          { canonical_id: Number(canonicalId), secondary_ids: secondaryIds },
        )
        if (data && data.success) {
          this.$notify.success('warehouse_material_merge_success')
          this.$emit('merged', { canonicalId: Number(canonicalId), secondaryIds })
          await this.loadDuplicates()
        } else {
          this.$notify.error('save_error')
        }
      } catch (e) {
        this.$notify.error('save_error')
      } finally {
        this.isMerging = false
        this.mergingGroup = null
      }
    },
  },
}
</script>
